<template>
  <div>
    <div v-if="!isShsSchool" class="mx-6 mx-md-12 pt-4">
      <div class="d-flex align-start">
        <v-avatar class="icon__home" height="46">
          <v-icon>mdi-home-outline</v-icon>
        </v-avatar>
        <span class="heading-text ml-3 mt-3">
          100-point residential address check</span
        >
      </div>
      <div class="mt-6 max-width-808">
        <p class="text-16-24">
          Schools that are near or above their buffer level should use the
          100-point residential address check to determine the student's
          entitlement to enrol at the school. The residential address check does
          not need to be used for non-local enrolment applications.
        </p>

        <p class="text-16-24">
          Principals may waive or vary the 100-point residential address check
          requirements, to meet the needs of the school community or where a
          parent can give good reasons why they cannot present documents.
        </p>

        <p class="text-16-24">
          When you turn on this setting, parents and carers will be asked to
          provide extra documents with their online enrolment form.
        </p>
      </div>
      <v-form ref="sreForm" class="mt-8">
        <OesCheckbox
          v-model="hasHundredPointCheck"
          :readonly="isSuperAdmin"
          @change="sethundredPointCheck()"
          data-testid="hundred-point-checkbox"
        >
          <template slot="label">
            <span class="hpc__label"
              >Request 100 points of documents for residential address
              verification</span
            >
          </template>
        </OesCheckbox>
      </v-form>
    </div>
    <v-divider v-if="showDocAddInstrFeature && !isShsSchool" />
    <div v-if="showDocAddInstrFeature" class="mx-6 mx-md-12 pt-4">
      <div class="d-flex align-start">
        <v-avatar class="icon__home" height="46">
          <v-icon>mdi-tag-outline</v-icon>
        </v-avatar>
        <span class="heading-text ml-3 mt-3"> Requested document types</span>
      </div>
      <div class="py-6">
        <p class="pb-4 max-width-750">
          Parents/carers will be instructed to produce digital or physical
          copies of the following document types. Additional instructions can be
          added to individual document types as appropriate.
        </p>
        <v-card class="max-width-808">
          <v-list class="py-0" data-testid="app-settings-doctype-list">
            <v-list-item
              v-for="documentType in documentTypes"
              :key="documentType.key"
              class="app-settings__doctype-row mr-0"
              two-line
              data-testid="app-settings-doctype-list-item"
            >
              <v-list-item-content>
                <v-list-item-title class="d-flex pl-3 text-wrap">
                  <div class="d-flex pt-4 pb-4">
                    <v-icon
                      class="mr-3 align-start"
                      role="img"
                      aria-hidden="false"
                      >{{ documentType.icon }}</v-icon
                    >
                    <strong class="mt-1 doctype__label">
                      {{ documentType.label }}
                      {{
                        hasHundredPointCheck &&
                        documentType.key === 'residentialAddress'
                          ? '(100 points required)'
                          : ''
                      }}
                    </strong>
                  </div>
                  <v-spacer />
                </v-list-item-title>
                <v-list-item-subtitle class="app-settings__doctype-description">
                  <p
                    v-if="documentType.description"
                    class="ml-11 pb-3 doctype__label"
                    v-html="documentType.description"
                  ></p>
                  <details
                    class="ml-11 mb-6 doctype__label"
                    v-if="
                      hasHundredPointCheck &&
                      documentType.key === 'residentialAddress'
                    "
                  >
                    <summary data-testid="accepted-documents-table">
                      <strong>Accepted documents table</strong>
                    </summary>
                    <HundredPointTable class="mb-4 mr-8" />
                  </details>
                  <AdsTextArea
                    v-model="documentType.additionalInstruction"
                    label="Additional parent/carer instructions"
                    :outlined="true"
                    :rows="3"
                    :counter="250"
                    maxlength="250"
                    persistent-placeholder
                    hint="Text entered here will be shown to the parent/carer when uploading their documents."
                    persistent-hint
                    data-testId="app-settings-additional-instruction"
                  />
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import OesCheckbox from '@/components/app/OesCheckbox'
import HundredPointTable from '@/components/app/HundredPointTable'
import { AdsTextArea } from '@nswdoe/doe-ui-core'
import settingsUtils from '@/helpers/settingsUtils'

export default {
  name: 'AppSettingsSupportingDocs',
  components: { OesCheckbox, HundredPointTable, AdsTextArea },
  props: {
    isShsSchool: {
      type: Boolean,
      default: false
    },
    hundredPointCheck: {
      type: Boolean,
      default: false
    },
    documentTypes: {
      type: Array,
      default: () => []
    },
    showDocAddInstrFeature: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hasHundredPointCheck: this.hundredPointCheck || false
    }
  },
  computed: {
    isSuperAdmin() {
      return settingsUtils.isSuperAdmin()
    }
  },
  methods: {
    sethundredPointCheck() {
      this.$emit('updatedHundredPointCheck', this.hasHundredPointCheck)
    }
  }
}
</script>

<style lang="scss" scoped>
.icon__home {
  background: $ads-light-blue;
  color: $ads-navy;
}
.heading-text {
  font-size: 20px;
  line-height: 24px;
  font-weight: bold;
  color: $color-primary;
  padding-left: 11px;
}
.max-width-808 {
  max-width: 808px;
}
.max-width-750 {
  max-width: 750px;
}
.text-16-24 {
  font-size: 16px;
  line-height: 24px;
}
.hpc__label {
  font-size: 1rem;
  color: $ads-dark;
}
.app-settings__doctype-row {
  min-height: 60px;
  &:not(:last-child) {
    border-bottom: 1px solid $ads-light-20;
  }
  .doctype__label {
    font-size: 1rem;
    color: $ads-dark;
  }
}
::v-deep .app-settings__doctype-description .v-text-field.v-textarea {
  max-width: 700px;
  margin-left: 44px;
  margin-top: 5px;
  .v-label {
    font-weight: bold;
    font-size: 1.2rem;
  }
  textarea {
    color: $ads-dark-70;
  }
}
::v-deep .app-settings__doctype-description a {
  font-weight: bold !important;
  text-decoration-skip-ink: none;
}
</style>
